import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { auditApi, type AuditEvent } from '@/api/audit';
import { ApiError } from '@/api/client';
import { formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';

type Category = 'all' | 'auth' | 'credential' | 'note' | 'tenant' | '2fa';

const CATEGORIES: Array<{ value: Category; labelKey: string; prefix?: string }> = [
  { value: 'all', labelKey: 'audit.all' },
  { value: 'auth', labelKey: 'audit.auth_category', prefix: 'auth.' },
  { value: '2fa', labelKey: 'audit.two_factor_category', prefix: 'auth.2fa.' },
  { value: 'credential', labelKey: 'audit.credentials_category', prefix: 'credential.' },
  { value: 'note', labelKey: 'audit.notes_category', prefix: 'note.' },
  { value: 'tenant', labelKey: 'audit.tenant_category', prefix: 'tenant.' },
];

const PAGE_SIZE = 50;

export default function AuditPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ro';
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const myRole = useAuthStore(
    (s) => s.user?.tenants.find((x) => x.tenant_id === s.currentTenantId)?.role,
  );
  const [category, setCategory] = useState<Category>('all');

  const query = useInfiniteQuery({
    queryKey: ['audit', tenantId, category],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      auditApi.list(tenantId!, {
        limit: PAGE_SIZE,
        before: pageParam ?? undefined,
        action_prefix: CATEGORIES.find((c) => c.value === category)?.prefix,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_cursor : null,
    enabled: !!tenantId,
  });

  const allEvents = useMemo(
    () => query.data?.pages.flatMap((p) => p.events) ?? [],
    [query.data],
  );

  const grouped = useMemo(
    () => groupByDay(allEvents, (key) => humanDay(key, t, lang)),
    [allEvents, t, lang],
  );

  const canVerify = myRole === 'owner' || myRole === 'admin';

  if (!tenantId) {
    return (
      <p className="text-sm text-[var(--text-muted)]">{t('vault.no_tenant')}</p>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="display-title">{t('audit.title')}</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          {t('audit.subtitle')}
        </p>
      </div>

      {canVerify && <ChainVerifier tenantId={tenantId} />}

      <div className="flex gap-2 mb-4 flex-wrap">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            type="button"
            className={`btn-outline text-xs ${
              category === c.value ? 'ring-1 ring-[var(--accent)]' : ''
            }`}
            onClick={() => setCategory(c.value)}
          >
            {t(c.labelKey)}
          </button>
        ))}
      </div>

      {query.isLoading && (
        <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>
      )}

      {query.isError && (
        <div
          className="text-sm rounded px-3 py-2"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
          }}
        >
          {t('common.error')}:{' '}
          {query.error instanceof ApiError
            ? query.error.detail
            : String(query.error)}
        </div>
      )}

      {allEvents.length === 0 && !query.isLoading && !query.isError && (
        <div className="card text-center py-12">
          <p className="text-sm text-[var(--text-muted)]">
            {t('audit.empty')}
          </p>
        </div>
      )}

      {grouped.length > 0 && (
        <div className="space-y-6">
          {grouped.map(({ label, events }) => (
            <DaySection key={label} label={label} events={events} />
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <div className="text-center mt-6">
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? t('audit.loading_more') : t('audit.load_more')}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Day groups
// ============================================================

interface DayGroupData {
  label: string;
  events: AuditEvent[];
}

function groupByDay(
  events: AuditEvent[],
  humanLabel: (key: string) => string,
): DayGroupData[] {
  const groups: Record<string, AuditEvent[]> = {};
  const order: string[] = [];
  for (const e of events) {
    const key = dayKey(e.created_at);
    if (!(key in groups)) {
      groups[key] = [];
      order.push(key);
    }
    groups[key].push(e);
  }
  return order.map((key) => ({
    label: humanLabel(key),
    events: groups[key],
  }));
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function humanDay(key: string, t: (k: string) => string, lang: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays === 0) return t('audit.today');
  if (diffDays === 1) return t('audit.yesterday');
  if (diffDays < 7) {
    return date.toLocaleDateString(lang, { weekday: 'long' });
  }
  return date.toLocaleDateString(lang, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function DaySection({ label, events }: DayGroupData) {
  return (
    <div>
      <div
        className="text-xs font-medium uppercase mb-2 tracking-wider"
        style={{ color: 'var(--text-dim)' }}
      >
        {label} · {events.length}
      </div>
      <div className="card p-0 overflow-hidden">
        {events.map((ev, idx) => (
          <EventRow key={ev.id} ev={ev} divider={idx < events.length - 1} />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Event row
// ============================================================

function EventRow({ ev, divider }: { ev: AuditEvent; divider: boolean }) {
  const { t, i18n } = useTranslation();
  const { icon, color, summary, resourceLink } = describe(ev, t);
  const dt = new Date(ev.created_at);
  const timeStr = dt.toLocaleTimeString(i18n.resolvedLanguage || i18n.language || 'ro', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const fullStr = formatDateTime(ev.created_at);

  return (
    <div
      className="flex items-start gap-3 px-4 py-3"
      style={divider ? { borderBottom: '1px solid var(--border)' } : undefined}
    >
      <div
        className="mt-0.5 w-7 h-7 rounded grid place-items-center flex-shrink-0"
        style={{
          background: `${color}22`,
          color,
          border: `1px solid ${color}55`,
        }}
      >
        <span className="text-xs">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px]">
          <span className="font-medium">{ev.user_email ?? t('admin_ui.audit.system')}</span>{' '}
          <span style={{ color: 'var(--text-muted)' }}>{summary}</span>
          {resourceLink && (
            <>
              {' '}
              <Link
                to={resourceLink.to}
                className="font-mono text-xs hover:underline"
                style={{ color: 'var(--accent)' }}
              >
                {resourceLink.label}
              </Link>
            </>
          )}
        </div>
        <div
          className="text-[11px] mt-0.5 flex items-center gap-2 font-mono"
          style={{ color: 'var(--text-dim)' }}
        >
          <span title={fullStr}>{timeStr}</span>
          <span>·</span>
          <span>{ev.action}</span>
          {ev.ip && (
            <>
              <span>·</span>
              <span>{ev.ip}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Action → human description + icon/color
// ============================================================

interface Described {
  icon: string;
  color: string;
  summary: string;
  resourceLink?: { to: string; label: string };
}

const COLORS = {
  auth: '#3b82f6',
  credential: '#d97706',
  note: '#7c3aed',
  tenant: '#14b8a6',
  share: '#d4537e',
  danger: '#dc2626',
  default: '#64748b',
};

function describe(ev: AuditEvent, t: TFunction): Described {
  const { action, extra, resource_type, resource_id } = ev;
  const title =
    (typeof extra['title'] === 'string' && extra['title']) ||
    (typeof extra['name'] === 'string' && extra['name']) ||
    null;

  const linkFor = (type: string | null, id: string | null, label: string) => {
    if (!type || !id) return undefined;
    if (type === 'credential') return { to: `/vault?open=${id}`, label };
    if (type === 'note') return { to: `/notes?open=${id}`, label };
    return undefined;
  };

  const labelText = title ?? (resource_id ? resource_id.slice(0, 8) : '');
  const link = labelText ? linkFor(resource_type, resource_id, labelText) : undefined;

  // Auth
  if (action === 'auth.register') {
    return { icon: '👤', color: COLORS.auth, summary: t('admin_ui.audit.auth_register') };
  }
  if (action === 'auth.login') {
    const summary = extra['used_2fa']
      ? t('admin_ui.audit.auth_login_2fa')
      : extra['used_recovery']
        ? t('admin_ui.audit.auth_login_recovery')
        : t('admin_ui.audit.auth_login');
    return { icon: '→', color: COLORS.auth, summary };
  }
  if (action === 'auth.2fa.enabled') {
    return { icon: '🔐', color: COLORS.auth, summary: t('admin_ui.audit.twofa_enabled') };
  }
  if (action === 'auth.2fa.disabled') {
    return { icon: '🔓', color: COLORS.danger, summary: t('admin_ui.audit.twofa_disabled') };
  }
  if (action === 'auth.2fa.recovery_codes_regenerated') {
    return {
      icon: '↻',
      color: COLORS.auth,
      summary: t('admin_ui.audit.twofa_recovery_codes_regenerated'),
    };
  }

  // Credentials
  if (action === 'credential.create')
    return { icon: '+', color: COLORS.credential, summary: t('admin_ui.audit.credential_create'), resourceLink: link };
  if (action === 'credential.update')
    return { icon: '✎', color: COLORS.credential, summary: t('admin_ui.audit.credential_update'), resourceLink: link };
  if (action === 'credential.delete')
    return { icon: '🗑', color: COLORS.danger, summary: t('admin_ui.audit.credential_delete'), resourceLink: link };
  if (action === 'credential.restore')
    return { icon: '↺', color: COLORS.credential, summary: t('admin_ui.audit.credential_restore'), resourceLink: link };
  if (action === 'credential.purge')
    return { icon: '✕', color: COLORS.danger, summary: t('admin_ui.audit.credential_purge') };
  if (action === 'credential.rotate')
    return { icon: '↻', color: COLORS.credential, summary: t('admin_ui.audit.credential_rotate'), resourceLink: link };
  if (action === 'credential.share_add')
    return { icon: '⇢', color: COLORS.share, summary: t('admin_ui.audit.credential_share_add'), resourceLink: link };
  if (action === 'credential.share_revoke')
    return { icon: '⇠', color: COLORS.danger, summary: t('admin_ui.audit.credential_share_revoke'), resourceLink: link };

  // Notes
  if (action === 'note.create')
    return { icon: '+', color: COLORS.note, summary: t('admin_ui.audit.note_create'), resourceLink: link };
  if (action === 'note.update')
    return { icon: '✎', color: COLORS.note, summary: t('admin_ui.audit.note_update'), resourceLink: link };
  if (action === 'note.rename')
    return { icon: '✎', color: COLORS.note, summary: t('admin_ui.audit.note_rename'), resourceLink: link };
  if (action === 'note.move')
    return { icon: '↔', color: COLORS.note, summary: t('admin_ui.audit.note_move'), resourceLink: link };
  if (action === 'note.delete')
    return { icon: '🗑', color: COLORS.danger, summary: t('admin_ui.audit.note_delete'), resourceLink: link };
  if (action === 'note.restore')
    return { icon: '↺', color: COLORS.note, summary: t('admin_ui.audit.note_restore'), resourceLink: link };
  if (action === 'note.purge')
    return { icon: '✕', color: COLORS.danger, summary: t('admin_ui.audit.note_purge') };
  if (action === 'note.rotate')
    return { icon: '↻', color: COLORS.note, summary: t('admin_ui.audit.note_rotate'), resourceLink: link };
  if (action === 'note.share_add')
    return { icon: '⇢', color: COLORS.share, summary: t('admin_ui.audit.note_share_add'), resourceLink: link };
  if (action === 'note.share_revoke')
    return { icon: '⇠', color: COLORS.danger, summary: t('admin_ui.audit.note_share_revoke'), resourceLink: link };
  if (action === 'asset.rewrap')
    return { icon: '⇢', color: COLORS.share, summary: t('admin_ui.audit.asset_rewrap'), resourceLink: link };

  // Tenant
  if (action === 'tenant.delete')
    return { icon: '✕', color: COLORS.danger, summary: t('admin_ui.audit.tenant_delete', { title: title ?? '—' }) };
  if (action === 'tenant.create')
    return { icon: '⬢', color: COLORS.tenant, summary: t('admin_ui.audit.tenant_create', { title: title ?? '—' }) };

  return { icon: '·', color: COLORS.default, summary: action };
}

/** Verificarea integrității lanțului de hash — la cerere, e o operație grea. */
function ChainVerifier({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const verify = useMutation({ mutationFn: () => auditApi.verify(tenantId) });
  const result = verify.data;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="audit-verify">
      <button
        type="button"
        className="btn-outline text-xs"
        disabled={verify.isPending}
        onClick={() => verify.mutate()}
      >
        {verify.isPending ? t('audit.verifying') : t('audit.verify')}
      </button>
      {result?.ok && (
        <span className="text-xs text-green-600 dark:text-green-400" data-testid="audit-verify-ok">
          {t('audit.verify_ok', { count: result.events_checked })}
        </span>
      )}
      {result && !result.ok && (
        <span className="text-xs text-red-600 dark:text-red-400" role="alert">
          {t('audit.verify_broken', { id: result.first_broken_event_id })}
        </span>
      )}
      {verify.isError && (
        <span className="text-xs text-red-600 dark:text-red-400" role="alert">
          {verify.error instanceof ApiError ? verify.error.detail : t('audit.verify_error')}
        </span>
      )}
    </div>
  );
}
